/**
 * Per-staff price-exception editor (DNKADM-0055 U3) — manages the
 * `v2_staff_variation_price` rows under ONE variation (handoff §5.5).
 *
 * For a variation, an admin may override the price for a specific assigned staff member
 * (money-only — never credits, §Non-Goals). Each row is unique on `(staff, variation)`;
 * the editor offers only staff NOT already overridden, so a duplicate `(staff,
 * variation)` cannot be entered. Add upserts via `upsertVariationStaffPrice`; a row's
 * Remove deletes it (reverting to the variation's base price).
 *
 * Staff options come from the service's ELIGIBLE staff assignments (passed in as
 * `{ id, name }`), not the whole roster — only an assigned staffer can carry an override.
 */
import { useEffect, useId, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { IconAction } from '@/components/IconAction';
import { Icon } from '@/theme';
import {
  deleteVariationStaffPrice,
  listVariationStaffPrices,
  upsertVariationStaffPrice,
  type VariationStaffPrice,
} from '@/api/services';
import { formatMoney } from '@/utils/v2Format';
import type { ShowToast } from '@/components/toastContext';
import type { IdNameOption } from './AssignmentEditor';
import { classifyFormError } from '@/api/screenError';

export interface StaffPriceEditorProps {
  serviceId: string;
  variationId: string;
  /** The service's eligible staff (only these may carry an override). */
  staffOptions: IdNameOption[];
  /** The currency code, or null while it loads. */
  currency: string | null;
  showToast: ShowToast;
  /**
   * Fired after every successful price write (DNKADM-0304 U3) so sibling
   * surfaces — the panel's EffectivePriceView — re-resolve instead of showing
   * the pre-write price.
   */
  onChanged?: () => void;
}

/** Parse a price text field to a finite number ≥ 0, or null when invalid/blank. */
function parsePrice(text: string): number | null {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

export function StaffPriceEditor({
  serviceId,
  variationId,
  staffOptions,
  currency,
  showToast,
  onChanged,
}: StaffPriceEditorProps) {
  const ids = useId();
  const [rows, setRows] = useState<VariationStaffPrice[]>([]);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const [newStaffId, setNewStaffId] = useState('');
  const [newPrice, setNewPrice] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const loaded = await listVariationStaffPrices(serviceId, variationId);
        if (cancelled) return;
        setRows(loaded);
        setLoadError(false);
      } catch {
        if (cancelled) return;
        setLoadError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [serviceId, variationId, reloadKey]);

  const nameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of staffOptions) map.set(s.id, s.name || s.id);
    return map;
  }, [staffOptions]);

  // Only staff WITHOUT an existing override are addable (unique (staff, variation)).
  const overridden = useMemo(() => new Set(rows.map((r) => r.staffId)), [rows]);
  const addableStaff = useMemo(
    () => staffOptions.filter((s) => !overridden.has(s.id)),
    [staffOptions, overridden],
  );

  async function handleAdd() {
    setAddError(null);
    if (newStaffId === '') {
      setAddError('Choose a staff member.');
      return;
    }
    const price = parsePrice(newPrice);
    if (price === null) {
      setAddError('Enter a price (0 or more).');
      return;
    }
    setBusy(true);
    try {
      await upsertVariationStaffPrice(serviceId, variationId, newStaffId, price);
      setNewStaffId('');
      setNewPrice('');
      setReloadKey((k) => k + 1);
      onChanged?.();
      showToast('Staff price saved', 'success');
    } catch (err) {
      const view = classifyFormError(err, 'Couldn’t save the staff price.');
      if (view.kind === 'inline') setAddError(view.message);
      else showToast(view.message, 'error');
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove(staffId: string) {
    setBusy(true);
    try {
      await deleteVariationStaffPrice(serviceId, variationId, staffId);
      setReloadKey((k) => k + 1);
      onChanged?.();
      showToast('Staff price removed', 'success');
    } catch (err) {
      const view = classifyFormError(err, 'Couldn’t remove the staff price.');
      showToast(view.message, 'error');
    } finally {
      setBusy(false);
    }
  }

  const staffLabelId = `${ids}-staff-label`;

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
        Per-staff price exceptions
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
        Override this variation’s price for a specific eligible staff member (money only —
        credits are unchanged).
      </Typography>

      {loadError && (
        <Alert
          severity="error"
          sx={{ mb: 1, py: 0.5 }}
          action={
            <Button color="inherit" size="small" onClick={() => setReloadKey((k) => k + 1)}>
              Retry
            </Button>
          }
        >
          Couldn’t load the staff prices.
        </Alert>
      )}

      {rows.length > 0 && (
        <Table size="small" aria-label="Staff price exceptions" sx={{ mb: 1 }}>
          <TableHead>
            <TableRow>
              <TableCell>Staff</TableCell>
              <TableCell align="right">Price</TableCell>
              <TableCell align="right">Remove</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.staffId}>
                <TableCell>{nameById.get(row.staffId) ?? row.staffId}</TableCell>
                <TableCell align="right">
                  {currency ? formatMoney(row.price, currency) : row.price.toFixed(2)}
                </TableCell>
                <TableCell align="right">
                  <IconAction
                    size="small"
                    label={`Remove price override for ${nameById.get(row.staffId) ?? row.staffId}`}
                    disabled={busy}
                    onClick={() => void handleRemove(row.staffId)}
                  >
                    <Icon name="delete" />
                  </IconAction>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {staffOptions.length === 0 ? (
        <Alert severity="info" sx={{ py: 0.5 }}>
          Assign eligible staff to this service first to set per-staff prices.
        </Alert>
      ) : (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ alignItems: 'flex-start' }}>
          <FormControl size="small" sx={{ minWidth: 200 }} disabled={busy || addableStaff.length === 0}>
            <InputLabel id={staffLabelId}>Staff member</InputLabel>
            <Select
              labelId={staffLabelId}
              label="Staff member"
              value={newStaffId}
              onChange={(e) => setNewStaffId(e.target.value)}
              slotProps={{ input: { 'aria-label': 'Staff member' } }}
            >
              {addableStaff.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name || s.id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Price"
            type="number"
            value={newPrice}
            onChange={(e) => setNewPrice(e.target.value)}
            disabled={busy || addableStaff.length === 0}
            size="small"
            slotProps={{
              htmlInput: { 'aria-label': 'Override price', min: 0, step: '0.01', inputMode: 'decimal' },
              ...(currency ? { input: { startAdornment: <InputAdornment position="start">{currency}</InputAdornment> } } : {}),
            }}
          />
          <Button
            variant="outlined"
            size="small"
            onClick={() => void handleAdd()}
            disabled={busy || addableStaff.length === 0}
            sx={{ mt: 0.5 }}
          >
            Add override
          </Button>
        </Stack>
      )}

      {addableStaff.length === 0 && staffOptions.length > 0 && (
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
          Every eligible staff member already has an override.
        </Typography>
      )}

      {addError && (
        <Alert severity="error" role="alert" sx={{ mt: 1, py: 0.5 }}>
          {addError}
        </Alert>
      )}
    </Box>
  );
}
